import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Plus, Users, Clock, MapPin, MessageSquare, X, Trash2, ArrowLeft } from 'lucide-react';
import Card from '../components/UI/Card';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, startOfWeek, endOfWeek } from 'date-fns';

const Calendar = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [reservations, setReservations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const [loadingNotes, setLoadingNotes] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const [loadingCalendarData, setLoadingCalendarData] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [airbnbData, setAirbnbData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [vrboData, setVrboData] = useState(null);
  const [showMobileModal, setShowMobileModal] = useState(false);

  // Load listings from config file
  useEffect(() => {
    const loadListings = async () => {
      try {
        const response = await fetch('/properties.json');
        const config = await response.json();


        // Convert listings to properties format and validate
        const propertiesFromConfig = config.listings.map(listing => {
          // Validate that at least one iCal URL is provided
          if (!listing.airbnb_ical_url && !listing.vrbo_ical_url) {
            console.warn(`Property "${listing.name}" has no iCal URLs configured`);
          }

          return {
            id: listing.id,
            name: listing.name,
            address: listing.location,
            color: 'blue',
            airbnb_ical_url: listing.airbnb_ical_url,
            vrbo_ical_url: listing.vrbo_ical_url
          };
        });

        setProperties(propertiesFromConfig);

        // Check for listing parameter in URL, otherwise set first listing as default
        const listingParam = searchParams.get('listing');
        if (listingParam && config.listings.find(l => l.id === listingParam)) {
          setSelectedProperty(listingParam);
        } else if (config.listings.length > 0) {
          setSelectedProperty(config.listings[0].id);
        }

        setLoading(false);
      } catch (error) {
        console.error('Failed to load listings config:', error);
        setLoading(false);
      }
    };

    loadListings();
    loadCurrentUser();
  }, [searchParams]);

  const loadCurrentUser = async () => {
    try {
      // First try to get the authenticated user from sessionStorage
      const storedUser = sessionStorage.getItem('hostbuddy_current_user');
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
        return;
      }

      // Fallback: load users data and use first user (for backward compatibility)
      const response = await fetch('/users.json');
      const users = await response.json();
      if (users.length > 0) {
        setCurrentUser(users[0]);
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const currentMonth = startOfMonth(selectedDate);
  const monthEnd = endOfMonth(selectedDate);
  // Generate calendar grid with proper week alignment
  const calendarStart = startOfWeek(currentMonth, { weekStartsOn: 0 }); // Sunday = 0
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const monthDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Internal function to sync Airbnb calendar data
  const syncAirbnbCalendar = useCallback(async (propertyData) => {
    if (!propertyData || !propertyData.airbnb_ical_url) {
      return null;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/airbnb/fetch-calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ical_url: propertyData.airbnb_ical_url,
          property_name: propertyData.name,
          property_id: propertyData.id
        }),
      });

      const data = await response.json();

      if (data.success) {
        setAirbnbData(data);

        // Convert Airbnb events to reservations format
        const airbnbReservations = data.events.map((event, index) => ({
          id: `${event.uid || index}`,
          propertyId: propertyData.id,
          guestName: event.guest_name,
          checkIn: new Date(event.start_date),
          checkOut: new Date(event.end_date),
          guests: event.guest_count,
          notes: [], // Will be loaded separately
          status: event.status,
          source: 'airbnb',
          airbnbData: event
        }));

        return airbnbReservations;
      } else {
        console.error('Failed to sync Airbnb calendar:', data.message);
        return null;
      }
    } catch (error) {
      console.error('Error syncing Airbnb calendar:', error);
      return null;
    }
  }, []);
  // Internal function to sync VRBO calendar data
  const syncVrboCalendar = useCallback(async (propertyData) => {
    if (!propertyData || !propertyData.vrbo_ical_url) {
      return null;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/vrbo/fetch-calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ical_url: propertyData.vrbo_ical_url,
          property_name: propertyData.name,
          property_id: propertyData.id
        }),
      });

      const data = await response.json();

      if (data.success) {
        setVrboData(data);

        // Convert VRBO events to reservations format
        const vrboReservations = data.events.map((event, index) => ({
          id: `${event.uid || index}`,
          propertyId: propertyData.id,
          guestName: event.guest_name,
          checkIn: new Date(event.start_date),
          checkOut: new Date(event.end_date),
          guests: event.guest_count,
          notes: [], // Will be loaded separately
          status: event.status,
          source: 'vrbo',
          vrboData: event
        }));

        return vrboReservations;
      } else {
        console.error('Failed to sync VRBO calendar:', data.message);
        return null;
      }
    } catch (error) {
      console.error('Error syncing VRBO calendar:', error);
      return null;
    }
  }, []);
  // Auto-sync calendar data for the selected property
  const autoSyncCalendarData = useCallback(async (propertyId) => {
    if (!propertyId || !properties.length) return;

    const propertyData = properties.find(p => p.id === propertyId);
    if (!propertyData) return;

    setLoadingCalendarData(true);

    try {
      const [airbnbReservations, vrboReservations] = await Promise.all([
        syncAirbnbCalendar(propertyData),
        syncVrboCalendar(propertyData)
      ]);

      // Combine all reservations
      const allReservations = [
        ...(airbnbReservations || []),
        ...(vrboReservations || [])
      ];

      // Set reservations without notes (notes will be loaded on demand)
      const reservationsWithoutNotes = allReservations.map(reservation => ({
        ...reservation,
        notes: [] // Initialize with empty notes array
      }));
 
      setReservations(reservationsWithoutNotes);
    } catch (error) {
      console.error('Error auto-syncing calendar data:', error);
    } finally {
      setLoadingCalendarData(false);
    }
  }, [properties, syncAirbnbCalendar, syncVrboCalendar]);

  // Auto-sync when component mounts or selected property changes
  useEffect(() => {
    if (selectedProperty && properties.length > 0) {
      autoSyncCalendarData(selectedProperty);
    }
  }, [selectedProperty, properties, autoSyncCalendarData]);

  const loadNotesForReservation = useCallback(async (reservationId) => {
      setLoadingNotes(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/reservations/${reservationId}/notes`);
        if (response.ok) {
          const notesData = await response.json();
          // Update reservations array
          setReservations(prev => prev.map(res => 
            res.id === reservationId 
              ? { ...res, notes: notesData || [] }
              : res
          ));
          // Update selected reservation if it matches
          setSelectedReservation(prev => 
            prev && prev.id === reservationId 
              ? { ...prev, notes: notesData || [] }
              : prev
          );
          return true; // Return success status
        }
        return false;
      } catch (error) {
        console.error('Error loading notes for reservation:', reservationId, error);
        return false;
      } finally {
        setLoadingNotes(false);
      }
    }, []); // Empty dependency array since function doesn't depend on any props or state

   // Auto-load notes when reservation is selected
   useEffect(() => {
     if (selectedReservation) {
       // Load notes for selected reservation
       loadNotesForReservation(selectedReservation.id);
     }
  }, [selectedReservation?.id, loadNotesForReservation]); // Now safe to include loadNotesForReservation





  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const openImageModal = (imageUrl, title) => {
    setFullscreenImage({ url: imageUrl, title });
  };

  const closeImageModal = () => {
    setFullscreenImage(null);
  };

  const handleAddNote = async () => {
    if (selectedReservation && (newNote.trim() || selectedImage)) {
      try {
        const formData = new FormData();
        formData.append('reservation_id', selectedReservation.id);
        if (newNote.trim()) {
          formData.append('note_text', newNote.trim());
        }
        if (selectedImage) {
          formData.append('image', selectedImage);
        }
        if (currentUser) {
           formData.append('author_name', currentUser.name);
         }

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/reservations/` + selectedReservation.id + '/notes', {
           method: 'POST',
           body: formData,
         });

        if (response.ok) {
          // const data = await response.json(); // Response data not used
          
          // Reload notes for this reservation
           const notesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/reservations/${selectedReservation.id}/notes`);
           if (notesResponse.ok) {
             const notesData = await notesResponse.json();
             
             // Update local state
             const updatedReservations = reservations.map(res =>
               res.id === selectedReservation.id
                 ? { ...res, notes: notesData }
                 : res
             );
             setReservations(updatedReservations);
             
             // Update selected reservation
             setSelectedReservation(prev => ({
               ...prev,
               notes: notesData
             }));
           }
          
          setNewNote('');
          setSelectedImage(null);
          setImagePreview(null);
          setShowNoteModal(false);
        } else {
          console.error('Failed to save note');
        }
      } catch (error) {
        console.error('Error saving note:', error);
      }
    }
  };

  const handleDeleteNoteClick = (note) => {
    setNoteToDelete(note);
    setShowDeleteConfirm(true);
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete || !noteToDelete.id) {
      console.error('Note ID is required');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notes/${noteToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Reload notes for this reservation
        const notesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/reservations/${selectedReservation.id}/notes`);
        if (notesResponse.ok) {
          const notesData = await notesResponse.json();
          
          // Update local state
          const updatedReservations = reservations.map(res =>
            res.id === selectedReservation.id
              ? { ...res, notes: notesData }
              : res
          );
          setReservations(updatedReservations);
          
          // Update selected reservation
          setSelectedReservation(prev => ({
            ...prev,
            notes: notesData
          }));
        }
        
        // Close confirmation dialog
        setShowDeleteConfirm(false);
        setNoteToDelete(null);
      } else {
        console.error('Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setNoteToDelete(null);
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" />
          <p className="text-gray-400 mt-4">Loading properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-start mb-4">
            <button
              onClick={() => navigate('/listings')}
              className="btn-glass p-2 hover:bg-white/10 transition-colors duration-200"
              title={t('common.back', 'Back')}
            >
              <ArrowLeft className="w-6 h-6 text-blue-400" />
            </button>
          </div>
          <h1 className="text-3xl font-bold text-white mb-4 text-center">{t('calendar.title')}</h1>

          {/* Property Display */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
            <div className="glass border border-white/20 rounded-lg px-4 py-2 text-white bg-transparent max-w-xs">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-blue-400" />
                <span className="font-medium">
                  {properties.find(p => p.id === selectedProperty)?.name || 'No Property Selected'}
                </span>
              </div>
            </div>

            {loadingCalendarData && (
              <div className="flex items-center space-x-2 text-gray-400">
                <LoadingSpinner size="sm" />
                <span className="text-sm">Loading calendar data...</span>
              </div>
            )}
          </div>

          {/* Month Selector - Left aligned on desktop, centered on mobile */}
          <div className="flex justify-center md:justify-start mb-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1))}
                className="btn-glass px-3 py-2"
              >
                ←
              </button>
              <span className="text-white font-medium px-4">
                {format(selectedDate, 'MMMM yyyy')}
              </span>
              <button
                onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1))}
                className="btn-glass px-3 py-2"
              >
                →
              </button>
            </div>
          </div>
        </div>



        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar Grid */}
          <div className="lg:col-span-2">
            <Card glass className="p-4 relative">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-gray-400 font-medium py-3 text-sm">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar with continuous reservation bars */}
              <div className="relative">
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {monthDays.map((day, dayIndex) => {
                    const isCurrentDay = isToday(day);
                    const isCurrentMonth = day.getMonth() === selectedDate.getMonth();
                    
                    // Check if this day is a checkout date for any reservation
                    const isCheckoutDate = reservations
                      .filter(r => r.propertyId === selectedProperty)
                      .some(reservation => {
                        const checkOutDate = new Date(reservation.checkOut);
                        return (
                          day.getDate() === checkOutDate.getDate() &&
                          day.getMonth() === checkOutDate.getMonth() &&
                          day.getFullYear() === checkOutDate.getFullYear()
                        );
                      });

                    return (
                      <div
                        key={day.toISOString()}
                        className={`min-h-[100px] p-2 border transition-all duration-200 relative ${
                          isCurrentDay
                            ? 'bg-blue-500/5 border-blue-400/20'
                            : isCurrentMonth
                            ? 'border-white/5 hover:border-white/10 hover:bg-white/[0.02]'
                            : 'border-white/5 hover:border-white/10 hover:bg-white/[0.02] opacity-30'
                        }`}
                      >
                        {/* Date number */}
                        <div className={`text-sm font-medium mb-2 relative z-20 ${
                          isCurrentDay ? 'text-blue-400' : isCurrentMonth ? 'text-white' : 'text-gray-500'
                        }`}>
                          {format(day, 'd')}
                        </div>
                        
                        {/* Checkout indicator */}
                         {isCheckoutDate && (
                           <div className="absolute top-1 right-1 z-30">
                             <div className="w-2 h-2 bg-orange-400 rounded-full" title="Checkout - Cleaning Required"></div>
                           </div>
                         )}
                      </div>
                    );
                  })}
                </div>

                {/* Continuous reservation bars overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  {(() => {
                    const renderedBars = [];

                    // Get all unique reservations for the selected property
                    const uniqueReservations = reservations.filter(r => r.propertyId === selectedProperty);

                    // Group days by weeks for positioning
                    const weeks = [];
                    for (let i = 0; i < monthDays.length; i += 7) {
                      weeks.push(monthDays.slice(i, i + 7));
                    }

                    // Process each reservation to create continuous bars
                    uniqueReservations.forEach((reservation, reservationIndex) => {
                      const isAirbnb = reservation.source === 'airbnb';
                      const isVrbo = reservation.source === 'vrbo';
                      const checkIn = new Date(reservation.checkIn);
                      const checkOut = new Date(reservation.checkOut);

                      // Check if the reservation is within the current month's view
                      if (checkOut < currentMonth || checkIn > monthEnd) {
                        return; // Skip reservations not in view
                      }

                      // Calculate which reservations overlap on the same dates to stack them
                      const overlappingReservations = uniqueReservations.filter(r => {
                        const rCheckIn = new Date(r.checkIn);
                        const rCheckOut = new Date(r.checkOut);
                        return (
                          (rCheckIn < checkOut && rCheckOut > checkIn) // overlapping date ranges
                        );
                      });
                      
                      // Sort overlapping reservations by check-in date for consistent stacking
                      overlappingReservations.sort((a, b) => new Date(a.checkIn) - new Date(b.checkIn));
                      const stackIndex = overlappingReservations.findIndex(r => r.id === reservation.id);

                      // Find the start and end days of the reservation within the current month's view
                      const startDayIndex = monthDays.findIndex(day => 
                        day.getDate() === checkIn.getDate() && day.getMonth() === checkIn.getMonth()
                      );
                      const endDayIndex = monthDays.findIndex(day => 
                        day.getDate() === checkOut.getDate() && day.getMonth() === checkOut.getMonth()
                      );

                      // Determine if the reservation starts or ends in this month
                      const isStartOfReservation = startDayIndex !== -1;
                      const isEndOfReservation = endDayIndex !== -1;

                      // Calculate bar position and dimensions
                      const barStartDay = isStartOfReservation ? startDayIndex : 0;
                      const barEndDay = isEndOfReservation ? endDayIndex : monthDays.length - 1;

                      const startWeekIndex = Math.floor(barStartDay / 7);
                      const endWeekIndex = Math.floor(barEndDay / 7);

                      // Create a bar for each week the reservation spans
                      for (let weekIndex = startWeekIndex; weekIndex <= endWeekIndex; weekIndex++) {
                        const weekStartDay = weekIndex * 7;
                        const weekEndDay = weekStartDay + 6;

                        const segmentStartDay = Math.max(barStartDay, weekStartDay);
                        const segmentEndDay = Math.min(barEndDay, weekEndDay);

                        const cellWidth = 100 / 7;
                        const barHeight = 28; // Height of reservation bar
                        const barVerticalSpacing = 32; // Space between stacked bars

                        // Determine rounded corners for this segment
                        const roundedLeft = isStartOfReservation && weekIndex === startWeekIndex;
                        const roundedRight = isEndOfReservation && weekIndex === endWeekIndex;

                        let barLeft = (segmentStartDay % 7) * cellWidth;
                         let barWidth = (segmentEndDay - segmentStartDay + 1) * cellWidth;

                         // Add offset for start and end positions
                         if (roundedLeft) {
                           barLeft += cellWidth * 0.25; // Start at 1/4 of the cell
                           barWidth -= cellWidth * 0.25;
                         }

                         if (roundedRight) {
                           barWidth -= cellWidth * 0.75; // End at 1/4 of the end cell
                         }

                        // Calculate vertical position
                        const cellHeight = 100; // min-h-[100px] from calendar cell
                        const cellGap = 4; // gap-1
                        const cellPadding = 8; // p-2

                        const rowTop = weekIndex * (cellHeight + cellGap);
                        const baseCenterOffset = rowTop + cellPadding + (cellHeight - 2 * cellPadding - barHeight) / 2;
                        const topOffset = baseCenterOffset + (stackIndex * barVerticalSpacing);

                        const barKey = `${reservation.id}-bar-${weekIndex}`;

                        const isSelected = selectedReservation && selectedReservation.id === reservation.id;
                        
                        const handleReservationClick = () => {
                          setSelectedReservation(reservation);
                          // Open mobile modal on small screens
                          if (window.innerWidth < 1024) {
                            setShowMobileModal(true);
                          }
                        };

                        renderedBars.push(
                          <div
                            key={barKey}
                            onClick={handleReservationClick}
                            className={`
                              absolute cursor-pointer transition-all duration-200 hover:brightness-110
                              pointer-events-auto shadow-lg
                              ${isSelected 
                                 ? 'border-2 border-blue-300 shadow-blue-300/30 shadow-lg ring-1 ring-blue-300/20 brightness-110' 
                                 : 'border border-white/20'
                               }
                              ${isAirbnb ? 'bg-green-500 hover:bg-green-400' : isVrbo ? 'bg-pink-500 hover:bg-pink-400' : 'bg-blue-500 hover:bg-blue-400'}
                              ${roundedLeft ? 'rounded-l-full' : ''}
                              ${roundedRight ? 'rounded-r-full' : ''}
                            `}
                            style={{
                              left: `${barLeft}%`,
                              width: `${barWidth}%`,
                              height: `${barHeight}px`,
                              top: `${topOffset}px`,
                              zIndex: isSelected ? 50 + stackIndex : 10 + stackIndex
                            }}
                            title={`${reservation.guestName || 'Guest'} - ${format(checkIn, 'MMM d')} to ${format(checkOut, 'MMM d')}`}
                          >
                            </div>
                         );
                      }
                    });

                    return renderedBars;
                  })()}
                </div>

                {/* No data message */}
                {!loadingCalendarData && reservations.filter(r => r.propertyId === selectedProperty).length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No reservations found for this property</p>
                      <p className="text-xs mt-1">Calendar data will appear here when available</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Legend */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-3 bg-green-500 rounded-full border border-white/20"></div>
                    <span className="text-gray-300 text-sm">Airbnb</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-3 bg-pink-500 rounded-full border border-white/20"></div>
                    <span className="text-gray-300 text-sm">VRBO</span>
                  </div>
                  <div className="flex items-center space-x-2">
                     <div className="w-3 h-3 bg-orange-400 rounded-full border border-white/20"></div>
                     <span className="text-gray-300 text-sm">Checkout - Cleaning Required</span>
                   </div>
                </div>

              </div>

              {/* Loading overlay */}
              {loadingCalendarData && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                  <div className="text-center">
                    <LoadingSpinner size="lg" />
                    <p className="text-white mt-2">Syncing calendar data...</p>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Reservation Details - Hidden on mobile, shown as sidebar on desktop */}
          <div className="hidden lg:block space-y-6">
            {selectedReservation ? (
              <Card glass className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                  <span>Reservation Details</span>
                  {selectedReservation.source === 'airbnb' && (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                      Airbnb
                    </span>
                  )}
                  {selectedReservation.source === 'vrbo' && (
                    <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full">
                      VRBO
                    </span>
                  )}
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Users className="w-5 h-5 text-blue-400" />
                    <div>
                      <div className="text-white font-medium">{selectedReservation.guestName}</div>
                      <div className="text-gray-400 text-sm">
                        {selectedReservation.guests} guests
                        {selectedReservation.source === 'airbnb' && selectedReservation.airbnbData && (
                          <span className="ml-2">• Status: {selectedReservation.airbnbData.status}</span>
                        )}
                        {selectedReservation.source === 'vrbo' && selectedReservation.vrboData && (
                          <span className="ml-2">• Status: {selectedReservation.vrboData.status}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-blue-400" />
                    <div>
                      <div className="text-white font-medium">
                        {properties.find(p => p.id === selectedReservation.propertyId)?.name}
                      </div>
                      <div className="text-gray-400 text-sm">
                        {properties.find(p => p.id === selectedReservation.propertyId)?.address}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <div>
                      <div className="text-white font-medium">
                        {format(selectedReservation.checkIn, 'MMM d')} - {format(selectedReservation.checkOut, 'MMM d')}
                      </div>
                      <div className="text-gray-400 text-sm">
                        {Math.ceil((selectedReservation.checkOut - selectedReservation.checkIn) / (1000 * 60 * 60 * 24))} nights
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-medium text-white">Notes</h4>
                    <button
                      onClick={() => setShowNoteModal(true)}
                      className="btn-glass px-3 py-1 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Loading State */}
                  {loadingNotes && (
                    <div className="bg-white/5 rounded-lg p-4 flex items-center justify-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2 text-gray-300 text-sm">Loading notes...</span>
                    </div>
                  )}

                  {/* Notes Display */}
                  {!loadingNotes && (
                    <div className="space-y-3">
                      {selectedReservation.notes.length > 0 ? (
                        selectedReservation.notes.map((note, index) => (
                          <div key={index} className="bg-white/5 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <MessageSquare className="w-5 h-5 text-blue-400 mt-0.5" />
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-white font-medium text-sm">
                                      {typeof note === 'object' ? (note.author_name || 'Unknown User') : 'Unknown User'}:
                                    </span>
                                    <span className="text-gray-400 text-xs">
                                      {typeof note === 'object' && note.created_at 
                                        ? new Date(note.created_at).toLocaleDateString()
                                        : 'Unknown Date'
                                      }
                                    </span>
                                  </div>
                                  {typeof note === 'object' && note.id && (
                                    <button
                                      onClick={() => handleDeleteNoteClick(note)}
                                      className="text-red-400 hover:text-red-300 transition-colors p-1 rounded hover:bg-red-500/10"
                                      title="Delete note"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                                {typeof note === 'object' ? (
                                  <div>
                                    {note.note_text && (
                                      <p className="text-gray-300 text-sm mb-2">{note.note_text}</p>
                                    )}
                                    {note.image_path && (
                                      <img 
                                        src={note.image_path} 
                                        alt="Note attachment" 
                                        className="max-w-full w-auto h-auto max-h-64 object-contain rounded-lg border border-white/20 cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => openImageModal(note.image_path, 'Note attachment')}
                                      />
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-gray-300 text-sm">{note}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-500 text-sm italic">No notes yet</div>
                      )}
                    </div>
                  )}
                </div>


              </Card>
            ) : (
              <Card glass className="p-6 text-center">
                <CalendarIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">Select a reservation to view details</p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Add Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <Card glass className="w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-white mb-4">Add Note</h3>
            <div className="space-y-4">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Enter your note..."
                className="w-full h-32 glass border border-white/20 rounded-lg p-3 text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Attach Image (optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="w-full text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                />
                <div className="mt-2 text-gray-300 text-sm">
                  <span className="font-medium">Author:</span> {currentUser?.name || 'Unknown User'}
                </div>
                {imagePreview && (
                  <div className="mt-2">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="max-w-full h-32 object-cover rounded-lg border border-white/20"
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() && !selectedImage}
                className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Note
              </button>
              <button
                onClick={() => {
                  setShowNoteModal(false);
                  setNewNote('');
                  setSelectedImage(null);
                  setImagePreview(null);
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <Card glass className="w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-white mb-4">Delete Note</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete this note? This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleDeleteNote}
                className="btn-primary bg-red-500 hover:bg-red-600 flex-1"
              >
                Delete
              </button>
              <button
                onClick={handleCancelDelete}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Mobile Reservation Details Modal */}
      {showMobileModal && selectedReservation && (
        <div className="top-6 fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 lg:hidden">
          <Card glass className="w-full max-w-md max-h-[90vh] overflow-y-auto p-6 relative">
            {/* Close button */}
            <button
              onClick={() => setShowMobileModal(false)}
              className="absolute top-8 right-4 text-gray-400 hover:text-white transition-colors z-10 bg-black/50 rounded-full p-1 backdrop-blur-sm"
            >
              <X className="w-6 h-6" />
            </button>

            <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2 pr-8">
              <span>Reservation Details</span>
              {selectedReservation.source === 'airbnb' && (
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                  Airbnb
                </span>
              )}
              {selectedReservation.source === 'vrbo' && (
                <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full">
                  VRBO
                </span>
              )}
            </h3>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="text-white font-medium">{selectedReservation.guestName}</div>
                  <div className="text-gray-400 text-sm">
                    {selectedReservation.guests} guests
                    {selectedReservation.source === 'airbnb' && selectedReservation.airbnbData && (
                      <span className="ml-2">• Status: {selectedReservation.airbnbData.status}</span>
                    )}
                    {selectedReservation.source === 'vrbo' && selectedReservation.vrboData && (
                      <span className="ml-2">• Status: {selectedReservation.vrboData.status}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="text-white font-medium">
                    {properties.find(p => p.id === selectedReservation.propertyId)?.name}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {properties.find(p => p.id === selectedReservation.propertyId)?.address}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="text-white font-medium">
                    {format(selectedReservation.checkIn, 'MMM d')} - {format(selectedReservation.checkOut, 'MMM d')}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {Math.ceil((selectedReservation.checkOut - selectedReservation.checkIn) / (1000 * 60 * 60 * 24))} nights
                  </div>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-medium text-white">Notes</h4>
                <button
                  onClick={() => {
                    setShowNoteModal(true);
                  }}
                  className="btn-glass px-3 py-1 text-sm"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Loading State */}
              {loadingNotes && (
                <div className="bg-white/5 rounded-lg p-4 flex items-center justify-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-gray-300 text-sm">Loading notes...</span>
                </div>
              )}

              {/* Notes Display */}
              {!loadingNotes && (
                <div className="space-y-3">
                  {selectedReservation.notes.length > 0 ? (
                    selectedReservation.notes.map((note, index) => (
                      <div key={index} className="bg-white/5 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <MessageSquare className="w-5 h-5 text-blue-400 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-white font-medium text-sm">
                                  {typeof note === 'object' ? (note.author_name || 'Unknown User') : 'Unknown User'}:
                                </span>
                                <span className="text-gray-400 text-xs">
                                  {typeof note === 'object' && note.created_at 
                                    ? new Date(note.created_at).toLocaleDateString()
                                    : 'Unknown Date'
                                  }
                                </span>
                              </div>
                              {typeof note === 'object' && note.id && (
                                <button
                                  onClick={() => handleDeleteNoteClick(note)}
                                  className="text-red-400 hover:text-red-300 transition-colors p-1 rounded hover:bg-red-500/10"
                                  title="Delete note"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            {typeof note === 'object' ? (
                              <div>
                                {note.note_text && (
                                  <p className="text-gray-300 text-sm mb-2">{note.note_text}</p>
                                )}
                                {note.image_path && (
                                  <img 
                                    src={note.image_path} 
                                    alt="Note attachment" 
                                    className="max-w-full w-auto h-auto max-h-64 object-contain rounded-lg border border-white/20 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => openImageModal(note.image_path, 'Note attachment')}
                                  />
                                )}
                              </div>
                            ) : (
                              <p className="text-gray-300 text-sm">{note}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-sm italic">No notes yet</div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeImageModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={fullscreenImage.url}
              alt={fullscreenImage.title}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
